/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * FeedHome
 * --------
 * /feed — the athlete's home: their UpVibe activities as posts (or a compact
 * list), filterable by sport and searchable, 20 at a time, with the training
 * card, next registered event and suggested events in the right rail — those
 * three come from one analytics run-batch request (useAthleteSummary).
 *
 * /feed/activity/:activityId renders the same page with the detail panel open,
 * so an activity has a URL that can be bookmarked or shared.
 *
 * Filtering and search apply to the activities loaded so far; "Show more"
 * keeps paging in. Posts are already shaped for the social feed to come.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import FeedIcon from '../../components/feed/FeedIcon';
import { ActivityPost, ActivityRow } from '../../components/feed/ActivityPost';
import TrainingCard from '../../components/feed/TrainingCard';
import ActivityDrawer from '../../components/feed/ActivityDrawer';
import { NextEventCard, SuggestedEvents, daysUntil } from '../../components/feed/EventCards';
import { FeedSkeleton, Onboarding, EmptyFilter, LoadError, GetTheApp } from '../../components/feed/FeedParts';
import { RouteGateProvider } from '../../components/feed/RouteGate';
import {
  useFeedUser, useActivityFeed, useActivity, useAthleteSummary, useSuggestedEvents, useActivitySync,
  useRouteHydration,
} from './useFeedData';
import {
  TYPE_ORDER, typeMeta, groupByWeek, weekName, matchesFilter, lastSynced, formatDateTime,
} from './feedModel';
import { FEED_PATH, activityPath } from './feedRoutes';
import { idOf } from './feedApi';

const VIEW_KEY = 'feedView';

function readPref(key, fallback) {
  try { return window.localStorage.getItem(key) ?? fallback; } catch (e) { return fallback; }
}
function writePref(key, value) {
  try { window.localStorage.setItem(key, value); } catch (e) { /* private mode: keep in memory */ }
}

export default function FeedHome() {
  const navigate = useNavigate();
  const { activityId } = useParams();
  const user = useFeedUser();
  const feed = useActivityFeed(user.userId);
  // Start once the first page is in, so the first fingerprint matches what is on screen.
  useActivitySync(user.userId, !feed.isLoading && !feed.isError);
  // Routes already on this device go straight into the query cache, so those
  // cards draw at once and never ask the server again.
  useRouteHydration(user.userId, feed.activities);
  const summary = useAthleteSummary(user.userId);
  const suggested = useSuggestedEvents(user.userId !== null);

  const [type, setType] = useState('all');
  const [query, setQuery] = useState('');
  const [view, setView] = useState(() => readPref(VIEW_KEY, 'cards'));

  const best = useMemo(() => summary.data?.best || new Map(), [summary.data]);
  const filtered = useMemo(() => feed.activities.filter((a) => matchesFilter(a, type, query)), [feed.activities, type, query]);
  const groups = useMemo(() => groupByWeek(filtered), [filtered]);
  const types = useMemo(() => {
    const present = new Set(feed.activities.map((a) => a.type));
    return TYPE_ORDER.filter((t) => present.has(t));
  }, [feed.activities]);

  const upcoming = useMemo(() => summary.data?.upcomingEvents || [], [summary.data]);
  const registeredIds = useMemo(() => new Set(upcoming.map((e) => idOf(e.id))), [upcoming]);
  const next = upcoming[0] ? { title: upcoming[0].title, days: daysUntil(upcoming[0]) } : null;

  const fromList = activityId ? feed.activities.find((a) => String(a.id) === String(activityId)) : null;
  const detail = useActivity(user.userId, activityId, fromList);

  const open = useCallback((a) => navigate(activityPath(a.id)), [navigate]);
  const close = useCallback(() => navigate(FEED_PATH), [navigate]);
  const chooseView = (v) => { setView(v); writePref(VIEW_KEY, v); };
  const clearFilters = () => { setType('all'); setQuery(''); };

  const empty = !feed.isLoading && !feed.isError && feed.activities.length === 0;
  const synced = useMemo(() => lastSynced(feed.activities), [feed.activities]);

  let list;
  if (feed.isLoading) list = <FeedSkeleton />;
  else if (feed.isError) list = <LoadError onRetry={feed.refetch} />;
  else if (empty) list = <Onboarding />;
  else if (!filtered.length) list = <EmptyFilter query={query} onClear={clearFilters} />;
  else {
    list = groups.map((g) => (
      <section key={g.start.getTime()} aria-label={weekName(g.start)}>
        <div className="fd-wsep">
          <span><b>{weekName(g.start)}</b> · {g.activities.length} {g.activities.length === 1 ? 'activity' : 'activities'} · {g.km.toFixed(1)} km</span>
        </div>
        {view === 'cards'
          ? g.activities.map((a) => <ActivityPost key={a.id} activity={a} user={user} best={best.get(a.id)} onOpen={open} />)
          : <div className="fd-clist">{g.activities.map((a) => <ActivityRow key={a.id} activity={a} best={best.get(a.id)} onOpen={open} />)}</div>}
      </section>
    ));
  }

  return (
    <RouteGateProvider>
      <main className="fd-main">
        <div className="fd-fhead">
          <h1>My activities</h1>
          {synced && <span className="fd-eyebrow" title={synced.toLocaleString()}><FeedIcon name="sync" />Last synced {formatDateTime(synced)}</span>}
        </div>


        {!empty && <TrainingCard summary={summary.data} isLoading={summary.isLoading} compact nextEvent={next} />}

        <div className="fd-seg">
          <span aria-current="true">All activities {feed.total > 0 && <span className="fd-cnt">{feed.total}</span>}</span>
          {!empty && (
            <div className="fd-views" role="group" aria-label="Layout">
              <button type="button" aria-pressed={view === 'cards'} aria-label="Cards" title="Cards" onClick={() => chooseView('cards')}><FeedIcon name="view_agenda" /></button>
              <button type="button" aria-pressed={view === 'compact'} aria-label="Compact list" title="Compact list" onClick={() => chooseView('compact')}><FeedIcon name="view_list" /></button>
            </div>
          )}
        </div>

        {!empty && !feed.isLoading && !feed.isError && (
          <div className="fd-tools">
            <div className="fd-chips" role="group" aria-label="Filter by sport">
              <button type="button" className="fd-chip" aria-pressed={type === 'all'} onClick={() => setType('all')}>
                All{feed.total > 0 && <span className="c">{feed.total}</span>}
              </button>
              {types.map((t) => (
                <button key={t} type="button" className="fd-chip" aria-pressed={type === t} onClick={() => setType(t)}>
                  <FeedIcon name={typeMeta(t).icon} />{typeMeta(t).label}
                </button>
              ))}
            </div>
            <label className="fd-search">
              <FeedIcon name="search" />
              <input type="search" placeholder="Search your activities" aria-label="Search your activities" value={query} onChange={(e) => setQuery(e.target.value)} />
            </label>
          </div>
        )}

        {list}

        {!feed.isLoading && !feed.isError && feed.hasNextPage && (
          <button type="button" className="fd-more" onClick={() => feed.fetchNextPage()} disabled={feed.isFetchingNextPage}>
            {feed.isFetchingNextPage ? 'Loading…' : 'Show more'}
            <span>{feed.activities.length} of {feed.total}</span>
          </button>
        )}
      </main>

      <aside className="fd-rail" aria-label="Your week and events">
        <TrainingCard summary={summary.data} isLoading={summary.isLoading} />
        <NextEventCard events={upcoming} isLoading={summary.isLoading} />
        <SuggestedEvents events={suggested.data} registeredIds={registeredIds} isLoading={suggested.isLoading} />
        <GetTheApp />
      </aside>

      {activityId && (
        <ActivityDrawer
          activity={detail.activity}
          isLoading={detail.isLoading}
          notFound={!detail.isLoading && !detail.activity}
          best={detail.activity ? best.get(detail.activity.id) : null}
          onClose={close}
        />
      )}
    </RouteGateProvider>
  );
}
